import { Link, useParams } from "wouter";
import { useGetQuotes, getGetQuotesQueryKey } from "@workspace/api-client-react";
import { QuoteCard } from "@/components/QuoteCard";
import { usePageMeta } from "@/hooks/usePageMeta";

function toCardQuote(q: { id: number; text: string; author: string; era: string; year?: number | null; tags: { name: string }[] }) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

export function TagPage() {
  const params = useParams<{ name: string }>();
  const tagName = decodeURIComponent(params.name ?? "");

  const queryParams = {
    tag: tagName,
    status: "approved" as const,
    pageSize: 100,
  };

  const { data, isLoading, isError } = useGetQuotes(queryParams, {
    query: { queryKey: getGetQuotesQueryKey(queryParams) },
  });

  const quotes = data?.data ?? [];

  const quoteCount = !isLoading ? quotes.length : null;
  usePageMeta({
    title:
      quoteCount !== null
        ? `${tagName} · ${quoteCount} quote${quoteCount !== 1 ? "s" : ""} — QuotedsArrow`
        : `${tagName} quotes — QuotedsArrow`,
    description:
      quoteCount !== null
        ? `Explore ${quoteCount} ${tagName} quote${quoteCount !== 1 ? "s" : ""} on QuotedsArrow.`
        : `Explore ${tagName} quotes on QuotedsArrow.`,
  });

  return (
    <section data-testid="page-tag">
      <div className="mb-6 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="mb-1 text-sm font-medium text-stone-500">Tag</div>
        <h2 className="text-2xl font-semibold tracking-tight text-stone-900">#{tagName}</h2>
        {!isLoading && (
          <p className="mt-1 text-sm text-stone-500">
            {quotes.length} quote{quotes.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="animate-pulse text-sm text-stone-400">Loading quotes…</p>
        </div>
      ) : isError ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">Could not load quotes for this tag.</p>
        </div>
      ) : quotes.length === 0 ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
          <p className="text-sm text-stone-500">No approved quotes found for "#{tagName}".</p>
          <Link href="/" className="mt-4 inline-block rounded-xl px-2 py-1 text-sm font-medium text-stone-900 transition hover:bg-stone-100 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1">
            ← Back to home
          </Link>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {quotes.map((q) => (
            <QuoteCard key={q.id} quote={toCardQuote(q)} />
          ))}
        </div>
      )}
    </section>
  );
}
